import * as z from "zod";

import { ANALYTICS_GROUP_NAMES } from "./analytics";

// posthog-js stores one `$groups` for every tab, so each event's groups come from the page it was captured on.

const PROJECT_PATH_PATTERN =
  /^\/(?:agent|shops|editor)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i;
const WORKSPACE_PATH_PATTERN =
  /^\/workspace\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i;
const SIGNED_OUT_PATH_PATTERN = /^\/(?:auth|signup|login)(?:[/?#]|$)/;

const PROJECT_WORKSPACES_STORAGE_KEY = "replo.analytics.projectWorkspaces";
const MAX_REMEMBERED_PROJECTS = 200;
const SENT_STORAGE_KEY_PREFIX = "replo.analytics.sent";
// Unchanged properties still go out daily, so a rename made in another browser lands within a day.
const DAY_MS = 24 * 60 * 60 * 1000;

type AnalyticsGroups = {
  [ANALYTICS_GROUP_NAMES.project]?: string;
  [ANALYTICS_GROUP_NAMES.workspace]?: string;
};

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be full or blocked; grouping falls back to the URL alone.
  }
}

const projectWorkspacesSchema = z.record(z.string(), z.string());

function readProjectWorkspaces(): Record<string, string> {
  try {
    return projectWorkspacesSchema.parse(
      JSON.parse(readStorage(PROJECT_WORKSPACES_STORAGE_KEY) ?? "{}"),
    );
  } catch {
    return {};
  }
}

/** Lets a project's pages carry its workspace before (or without) the workspace loading. */
export function rememberProjectWorkspace({
  projectId,
  workspaceId,
}: {
  projectId: string;
  workspaceId: string;
}) {
  const projectWorkspaces = readProjectWorkspaces();
  if (projectWorkspaces[projectId] === workspaceId) {
    return;
  }
  delete projectWorkspaces[projectId];
  const entries = [
    ...Object.entries(projectWorkspaces),
    [projectId, workspaceId],
  ];
  writeStorage(
    PROJECT_WORKSPACES_STORAGE_KEY,
    JSON.stringify(Object.fromEntries(entries.slice(-MAX_REMEMBERED_PROJECTS))),
  );
}

function pathOf(url: unknown): string | null {
  if (typeof url !== "string" || url.length === 0) {
    return null;
  }
  try {
    return new URL(url, "https://dashboard.replo.app").pathname;
  } catch {
    return null;
  }
}

function nonEmptyString(value: unknown): string | undefined {
  return z.string().min(1).safeParse(value).data;
}

/** Agent UI host events name their own project; others use the page URL. Signed-out pages get none. */
export function getEventGroups({
  properties,
  storedGroups,
  projectWorkspaces = readProjectWorkspaces(),
}: {
  properties: Record<string, unknown>;
  storedGroups: AnalyticsGroups;
  projectWorkspaces?: Record<string, string>;
}): AnalyticsGroups {
  const path = pathOf(properties.$current_url);
  if (path && SIGNED_OUT_PATH_PATTERN.test(path)) {
    return {};
  }
  const isAgentHostEvent = properties.surface === "agent-ui";
  const projectId =
    (isAgentHostEvent ? nonEmptyString(properties.projectId) : undefined) ??
    (path ? PROJECT_PATH_PATTERN.exec(path)?.[1] : undefined);
  const storedWorkspaceId = storedGroups[ANALYTICS_GROUP_NAMES.workspace];

  let workspaceId: string | undefined;
  if (projectId) {
    workspaceId =
      (isAgentHostEvent ? nonEmptyString(properties.workspaceId) : undefined) ??
      projectWorkspaces[projectId] ??
      // The stored workspace was written with its project, so it only applies to that project.
      (storedGroups[ANALYTICS_GROUP_NAMES.project] === projectId
        ? storedWorkspaceId
        : undefined);
  } else {
    workspaceId =
      (path ? WORKSPACE_PATH_PATTERN.exec(path)?.[1] : undefined) ??
      storedWorkspaceId;
  }

  return {
    ...(projectId ? { [ANALYTICS_GROUP_NAMES.project]: projectId } : {}),
    ...(workspaceId ? { [ANALYTICS_GROUP_NAMES.workspace]: workspaceId } : {}),
  };
}

type PostHogGroupClient = {
  group: (
    groupType: string,
    groupKey: string,
    groupPropertiesToSet?: Record<string, unknown>,
  ) => void;
  getGroups: () => Record<string, string>;
};

/** True (and recorded) when `value` has not gone out under `key` today. */
export function isNewToday(key: string, value: unknown) {
  const storageKey = `${SENT_STORAGE_KEY_PREFIX}.${key}`;
  const marker = JSON.stringify([Math.floor(Date.now() / DAY_MS), value]);
  if (readStorage(storageKey) === marker) {
    return false;
  }
  writeStorage(storageKey, marker);
  return true;
}

/** `posthog.group` sends `$groupidentify` on every call with properties; this sends only what changed today. */
export function setAnalyticsGroup(
  posthog: PostHogGroupClient,
  groupType: string,
  groupKey: string,
  groupProperties?: Record<string, unknown>,
) {
  if (
    groupProperties &&
    isNewToday(`${groupType}.${groupKey}`, groupProperties)
  ) {
    posthog.group(groupType, groupKey, groupProperties);
  } else if (posthog.getGroups()[groupType] !== groupKey) {
    posthog.group(groupType, groupKey);
  }
}
